// Where a term's pronunciation reaches: the places in the current Narration
// Scripts where the Render will speak it the way this entry says. A change to
// "say as" or the IPA applies to every one of them at once, so the Author sees
// how many before applying, and can read the sentences.

import type { StyleSheetPut, TermOccurrences } from "@contract";
import { buildPath } from "@contract";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../api/fetcher";
import { queryKeys } from "../../api/keys";
import { Button } from "../../components/control/Button";
import type { useBookDetail } from "../book/bookContext";

const DEBOUNCE_MS = 400;

/** The value once it has stopped changing for a moment, so typing does not ask per keystroke. */
function useSettled<T>(value: T): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [value]);
  return settled;
}

const fetchOccurrences = (bookId: string, terms: StyleSheetPut, detailTerm?: number) =>
  api<TermOccurrences>(buildPath("/books/:id/style-sheet/occurrences", { id: bookId }), {
    method: "POST",
    body: detailTerm === undefined ? { draft: terms } : { draft: terms, detailTerm },
  });

/** The counts for every term of the draft, one request for the whole list. */
export function useTermReach(bookId: string, draft: StyleSheetPut) {
  const settled = useSettled(JSON.stringify(draft.terms));
  return useQuery({
    queryKey: queryKeys.termOccurrences(bookId, settled),
    queryFn: () => fetchOccurrences(bookId, { ...draft, terms: JSON.parse(settled) }),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function TermReach({
  book,
  draft,
  termIndex,
  reach,
}: {
  book: ReturnType<typeof useBookDetail>;
  draft: StyleSheetPut;
  termIndex: number;
  reach: TermOccurrences["terms"][number] | undefined;
}) {
  const { t } = useTranslation("stylesheet");
  const [open, setOpen] = useState(false);
  const settled = useSettled(JSON.stringify(draft.terms));
  const detail = useQuery({
    queryKey: queryKeys.termOccurrences(book.id, settled, termIndex),
    queryFn: () => fetchOccurrences(book.id, { ...draft, terms: JSON.parse(settled) }, termIndex),
    enabled: open,
    placeholderData: keepPreviousData,
  });
  if (!reach) return null;
  if (reach.total === 0) return <p className="m-0 text-data text-ink-3">{t("reach.none")}</p>;
  const numberOf = (id: string) => book.chapters.find((c) => c.id === id)?.number ?? "–";
  const chapters = detail.data?.terms[termIndex]?.chapters ?? [];
  return (
    <div className="space-y-2">
      <p className="m-0 flex flex-wrap items-center gap-3 text-data text-ink-2">
        <span>
          {t("reach.count", { count: reach.total })}{" "}
          {t("reach.chapters", { count: reach.chapters.length })}
        </span>
        <Button variant="quiet" size="sm" onClick={() => setOpen((was) => !was)}>
          {open ? t("reach.hide") : t("reach.show")}
        </Button>
      </p>
      {open ? (
        <ul className="m-0 list-none space-y-2 p-0">
          {chapters.map((chapter) => (
            <li key={chapter.chapterId} className="space-y-1">
              <p className="label-mono m-0 text-ink-3">
                {t("reach.chapter", { number: numberOf(chapter.chapterId), count: chapter.count })}
              </p>
              {chapter.examples.map((example, at) => (
                // Examples never reorder, and two can read the same.
                // biome-ignore lint/suspicious/noArrayIndexKey: a fixed list of at most three.
                <p key={at} className="m-0 font-serif">
                  {example.snippet.clippedStart ? "… " : ""}
                  {example.snippet.before}
                  <mark className="bg-accent-soft text-ink">{example.snippet.match}</mark>
                  {example.snippet.after}
                  {example.snippet.clippedEnd ? " …" : ""}
                </p>
              ))}
              {chapter.count > chapter.examples.length ? (
                <p className="m-0 text-data text-ink-3">
                  {t("reach.more", { count: chapter.count - chapter.examples.length })}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
